import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Button, CircularProgress, CustomSnackbar, Dialog, IconButton, PageContainer, Paper, Stack, Tooltip, Typography, useIsMobile, useIsTablet } from '@pulse/ui';
import { useEffect, useState } from 'react';

import { getMondayOfWeek } from '../../delivery/utils/resourceLevelling';
import { TimesheetGrid } from '../components/TimesheetGrid';
import { TimesheetMobileList } from '../components/TimesheetMobileList';
import { WeeklyTotalCard } from '../components/WeeklyTotalCard';
import { WorkLogPanel } from '../components/WorkLogPanel';
import { useMyTimesheetWeek, useSubmitTimesheet } from '../hooks/useTimesheet';
import { buildDayColumns, parseLocalDate, toIsoDate, weekTotal } from '../utils/timesheetGrid';

function formatWeekLabel(weekStartIso: string, weekEndIso: string): string {
  const start = parseLocalDate(weekStartIso);
  const end = parseLocalDate(weekEndIso);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${start.getFullYear()}`;
}

// Below Home in the nav — every logged-in user has their own timesheet, same reasoning as Home
// (no permission gate). "Please use project UI/UX": the mockup's structure (day×task grid,
// side entry panel, week nav, weekly total widget) is followed, its visual styling is not.
export function TimesheetPage() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  // Tablet has room for the day×task grid, but not enough to comfortably show the grid and the
  // work log side panel together — so it keeps the grid and opens the panel as a Dialog, same
  // as mobile, instead of the desktop-only inline side panel.
  const workLogPanelAsDialog = isMobile || isTablet;
  const [weekStartIso, setWeekStartIso] = useState(() => toIsoDate(getMondayOfWeek(new Date())));
  const [selectedCell, setSelectedCell] = useState<{ issueId: string; dateIso: string } | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError } = useMyTimesheetWeek(weekStartIso);
  const submitMutation = useSubmitTimesheet();
  const isCurrentWeek = weekStartIso >= toIsoDate(getMondayOfWeek(new Date()));

  // Mobile's day strip defaults to today when the displayed week includes it, otherwise the
  // week's first day — deliberately re-derived only when the week itself changes (weekStartIso
  // alone doesn't change across the initial load, so this depends on the fetched week's start
  // date instead), not on every `data` refetch — a mutation like adding a work log invalidates
  // and refetches `data` with a new object identity but the same week, and that must not reset
  // the user's day selection mid-edit.
  const weekStart = data?.weekStart;
  useEffect(() => {
    if (!weekStart) return;
    const weekDays = buildDayColumns(weekStart);
    const todayIso = toIsoDate(new Date());
    setSelectedDay(weekDays.includes(todayIso) ? todayIso : weekDays[0] ?? weekStart);
  }, [weekStart]);

  function shiftWeek(deltaDays: number) {
    const current = parseLocalDate(weekStartIso);
    const next = new Date(current.getFullYear(), current.getMonth(), current.getDate() + deltaDays);
    setWeekStartIso(toIsoDate(next));
    setSelectedCell(null);
  }

  function submitProject(projectId: string) {
    submitMutation.mutate(
      { weekStart: weekStartIso, projectId },
      {
        onSuccess: () => setSuccessMessage('Timesheet submitted.'),
        onError: (err: Error) => setErrorMessage(err.message),
      },
    );
  }

  function submitWeek() {
    submitMutation.mutate(
      { weekStart: weekStartIso },
      {
        onSuccess: () => setSuccessMessage('Timesheet submitted for all projects this week.'),
        onError: (err: Error) => setErrorMessage(err.message),
      },
    );
  }

  const days = data ? buildDayColumns(data.weekStart) : [];
  // Nothing to submit if every relevant project is already Submitted or Approved.
  const hasSubmittableProject = data
    ? data.taskRows.some((t) => {
        const approval = data.projectApprovals.find((a) => a.projectId === t.projectId);
        return approval?.status !== 'Submitted' && approval?.status !== 'Approved';
      })
    : false;

  return (
    <PageContainer maxWidth="xl">
      <Stack spacing={3}>
        {(() => {
          const nextDisabledReason = isCurrentWeek ? "You can't view future weeks." : '';
          const isReadOnlyWeek = Boolean(data && !data.isEditable);
          const hasLockedProjects = Boolean(data && data.isEditable && data.projectApprovals.some((a) => a.status === 'Submitted' || a.status === 'Approved'));
          const dense = isMobile;
          const weekNav = (
            <Paper sx={dense ? { py: 0.5, display: 'flex', alignItems: 'center', gap: 0 } : { px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => shiftWeek(-7)} aria-label="Previous week" sx={dense ? { p: 0.5 } : undefined}>
                <ChevronLeftIcon fontSize={dense ? 'small' : 'medium'} />
              </IconButton>
              <Typography variant="body2" fontWeight={600} sx={dense ? { textAlign: 'center', whiteSpace: 'nowrap' } : { minWidth: 160, textAlign: 'center' }}>
                {data ? formatWeekLabel(data.weekStart, data.weekEnd) : ' '}
              </Typography>
              <Tooltip title={nextDisabledReason}>
                <span>
                  <IconButton size="small" onClick={() => shiftWeek(7)} aria-label="Next week" disabled={isCurrentWeek} sx={dense ? { p: 0.5 } : undefined}>
                    <ChevronRightIcon fontSize={dense ? 'small' : 'medium'} />
                  </IconButton>
                </span>
              </Tooltip>
              {isReadOnlyWeek && (
                <Tooltip title="This week is outside the current/previous week and is read-only.">
                  <LockIcon fontSize="small" color="disabled" />
                </Tooltip>
              )}
              {hasLockedProjects && (
                <Tooltip title="One or more projects this week have been submitted or approved and locked.">
                  <LockIcon fontSize="small" color="disabled" />
                </Tooltip>
              )}
            </Paper>
          );
          const submitButton = data && hasSubmittableProject ? (
            <Button variant="contained" size="small" startIcon={<SendIcon />} disabled={submitMutation.isPending} onClick={submitWeek}>
              Submit Week
            </Button>
          ) : null;

          return isMobile ? (
            <Stack spacing={1.5}>
              <Typography variant="h4" fontWeight={700}>Timesheet</Typography>
              <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="space-between" rowGap={1} columnGap={1}>
                {weekNav}
                {submitButton}
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
              <Typography variant="h4" fontWeight={700}>Timesheet</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {weekNav}
                {submitButton}
              </Stack>
            </Stack>
          );
        })()}

        {isLoading && <CircularProgress />}
        {isError && <Alert severity="error">Failed to load your timesheet. Please try again.</Alert>}

        {data && isMobile && (
          <Stack spacing={2}>
            <WeeklyTotalCard totalHours={weekTotal(data.workLogs)} targetHours={data.weekTargetHours} />
            <Paper sx={{ p: 1.5 }}>
              <TimesheetMobileList
                taskRows={data.taskRows}
                workLogs={data.workLogs}
                days={days}
                selectedDay={selectedDay ?? days[0] ?? data.weekStart}
                onSelectDay={setSelectedDay}
                selectedCell={selectedCell}
                onCellClick={(issueId, dateIso) => setSelectedCell({ issueId, dateIso })}
                projectApprovals={data.projectApprovals}
                onSubmitProject={submitProject}
                isSubmitting={submitMutation.isPending}
              />
            </Paper>
          </Stack>
        )}

        {data && !isMobile && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
              <Stack spacing={2}>
                <WeeklyTotalCard totalHours={weekTotal(data.workLogs)} targetHours={data.weekTargetHours} />
                <Paper sx={{ p: { xs: 0.5, md: 1.5 } }}>
                  <TimesheetGrid
                    taskRows={data.taskRows}
                    workLogs={data.workLogs}
                    days={days}
                    selectedCell={selectedCell}
                    onCellClick={(issueId, dateIso) => setSelectedCell({ issueId, dateIso })}
                    projectApprovals={data.projectApprovals}
                    onSubmitProject={submitProject}
                    isSubmitting={submitMutation.isPending}
                  />
                </Paper>
              </Stack>
            </Box>

            {selectedCell && !workLogPanelAsDialog && (
              <WorkLogPanel
                key={`${selectedCell.issueId}|${selectedCell.dateIso}`}
                taskRows={data.taskRows}
                workLogs={data.workLogs}
                selectedCell={selectedCell}
                projectApprovals={data.projectApprovals}
                onClose={() => setSelectedCell(null)}
                onError={setErrorMessage}
              />
            )}
          </Stack>
        )}

        {data && workLogPanelAsDialog && selectedCell && (
          <Dialog open onClose={() => setSelectedCell(null)} fullWidth maxWidth="xs">
            <WorkLogPanel
              key={`${selectedCell.issueId}|${selectedCell.dateIso}`}
              taskRows={data.taskRows}
              workLogs={data.workLogs}
              selectedCell={selectedCell}
              projectApprovals={data.projectApprovals}
              onClose={() => setSelectedCell(null)}
              onError={setErrorMessage}
              closeOnAdd
            />
          </Dialog>
        )}
      </Stack>

      <CustomSnackbar
        open={errorMessage !== null || successMessage !== null}
        onClose={() => { setErrorMessage(null); setSuccessMessage(null); }}
        message={errorMessage ?? successMessage}
        severity={errorMessage !== null ? 'error' : 'success'}
        autoHideDuration={4000}
      />
    </PageContainer>
  );
}
